import { useMutation, useQuery } from '@apollo/client';
import { Box, Card, LinearProgress, MenuItem, Typography } from '@mui/material';
import { FormSubmitRow } from 'components/common/form/submit-row';
import { LabelWithValue } from 'components/views/campaign/vendors/label-value';
import {
  CreateTcrCampaignMutation,
  CreateTcrCampaignViewQuery,
  UpdateTcrCampaignMutation,
} from 'components/views/campaign/vendors/tcr.graphql';
import { mergeReplacements } from 'components/views/company/outreach-template/outreach-template.util';
import { FormTextField } from 'components/views/forms/form-text-field';
import { useSnackbar } from 'providers/snackbar';
import { FormEvent, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { CreateTcrCampaign, CreateTcrCampaignVariables } from 'types/apollo/CreateTcrCampaign';
import {
  CreateTcrCampaignView,
  CreateTcrCampaignView_tcrDataForTcrCampaignCreation_existingCampaign,
  CreateTcrCampaignView_tcrDataForTcrCampaignCreation_mnos,
  CreateTcrCampaignView_tcrDataForTcrCampaignCreation_sharing,
  CreateTcrCampaignViewVariables,
} from 'types/apollo/CreateTcrCampaignView';
import { CreateTcrCampaignInput, UpdateTcrCampaignInput } from 'types/apollo/globalTypes';
import { UpdateTcrCampaign, UpdateTcrCampaignVariables } from 'types/apollo/UpdateTcrCampaign';
import { builtInReplacementMap } from '../../../../const/replacements';
import { TcrCampaignInfo, TcrSharingInfo } from './tcr-campaign-info';
import { Cnp, TcrCardProps, applyReplacements, defaultValues } from './tcr-constants';
import { TcrCheckboxGrid } from './tcr-form-fields';

const TcrForm = ({ campaignId, internalCampaignId }: TcrCardProps) => {
  const { openSnackbar } = useSnackbar();
  const [mnoOptions, setMnoOptions] = useState<
    CreateTcrCampaignView_tcrDataForTcrCampaignCreation_mnos[]
  >([]);
  const [existingCampaign, setExistingCampaign] =
    useState<CreateTcrCampaignView_tcrDataForTcrCampaignCreation_existingCampaign | null>(null);
  const [sharing, setSharing] =
    useState<CreateTcrCampaignView_tcrDataForTcrCampaignCreation_sharing | null>(null);

  const nonEditableForm = useForm<CreateTcrCampaignInput>({
    defaultValues: { ...defaultValues.input },
    mode: 'all',
  });

  const editableForm = useForm<UpdateTcrCampaignInput>({
    defaultValues: { ...defaultValues.editableFields },
    mode: 'all',
  });

  const updateValues = (
    existingCampaign: CreateTcrCampaignView_tcrDataForTcrCampaignCreation_existingCampaign,
  ) => {
    const nonEditableFormValues = nonEditableForm.getValues();
    const editableFormValues = editableForm.getValues();
    Object.entries(existingCampaign).forEach(([name, value]) => {
      if (Reflect.has(nonEditableFormValues, name)) {
        nonEditableForm.setValue(name as keyof CreateTcrCampaignInput, value ?? '');
      }
      if (Reflect.has(editableFormValues, name)) {
        editableForm.setValue(name as keyof UpdateTcrCampaignInput, value ?? '');
      }
    });
    setExistingCampaign(existingCampaign);
  };

  const { loading, refetch } = useQuery<CreateTcrCampaignView, CreateTcrCampaignViewVariables>(
    CreateTcrCampaignViewQuery,
    {
      variables: {
        campaignId: campaignId,
        internalCampaignId: internalCampaignId,
      },
      onCompleted: (data) => {
        if (data.campaign) {
          const [mergedReplacements] = mergeReplacements(
            {
              ...builtInReplacementMap,
              _COMPANY_: data.campaign.brand.company.name ?? '',
              _AGENT_NAME_: data.campaign.agentName ?? '[AGENT_NAME]',
            },
            data.campaign.schedulerFlow?.generator?.replacements ?? [],
          );
          const existingCampaign = data.tcrDataForTcrCampaignCreation.existingCampaign;
          if (existingCampaign) {
            updateValues(existingCampaign);
          } else if (data.campaign.dataForTcrCampaignCreation) {
            const sample1 = applyReplacements(
              mergedReplacements,
              data.campaign.dataForTcrCampaignCreation.sample1,
            );
            const sample2 = applyReplacements(
              mergedReplacements,
              data.campaign.dataForTcrCampaignCreation.sample2,
            );

            if (sample1) {
              editableForm.setValue('sample1', `${sample1} Text STOP to opt out.`);
            }
            if (sample2) {
              editableForm.setValue('sample2', `${sample2} Text STOP to opt out.`);
            }
            if (sample1.match(/https?:\/\//) || sample2.match(/https?:\/\//)) {
              nonEditableForm.setValue('embeddedLink', true);
            }
            setMnoOptions(data.tcrDataForTcrCampaignCreation.mnos);
            nonEditableForm.setValue(
              'mnos',
              data.tcrDataForTcrCampaignCreation.mnos.map((x) => x.networkId),
            );
          }
          setSharing(data.tcrDataForTcrCampaignCreation.sharing);
        }
      },
    },
  );

  const [createTcrCampaign] = useMutation<CreateTcrCampaign, CreateTcrCampaignVariables>(
    CreateTcrCampaignMutation,
  );
  const [updateTcrCampaign] = useMutation<UpdateTcrCampaign, UpdateTcrCampaignVariables>(
    UpdateTcrCampaignMutation,
  );

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (existingCampaign) {
      const result = await updateTcrCampaign({
        variables: {
          tcrCampaignId: existingCampaign.campaignId,
          editableFields: editableForm.getValues(),
        },
      });
      if (result.data?.updateTcrCampaign) {
        updateValues(result.data.updateTcrCampaign);

        await openSnackbar({
          message: `Updated campaign`,
          duration: 60000,
          severity: 'success',
        });
      }
    } else {
      const variables: CreateTcrCampaignVariables = {
        campaignId: internalCampaignId,
        input: nonEditableForm.getValues(),
        editableFields: editableForm.getValues(),
      };
      if (variables.input.usecase == 'LOW_VOLUME') {
        variables.input.subUsecases = ['MARKETING'];
      }
      const response = await createTcrCampaign({
        variables,
      });

      if (response.data) {
        const { campaignId, sharingStatus } = response.data.createTcrCampaign;
        await openSnackbar({
          message: `Created campaign with ID ${campaignId}. Sharing status: ${sharingStatus}`,
          duration: 60000,
          severity: 'success',
        });

        await refetch();
      }
    }
  };

  const title = existingCampaign ? 'Campaign created' : 'Create TCR Campaign';

  return (
    <form onSubmit={onSubmit}>
      {loading && <LinearProgress sx={{ height: '2px', mb: 1 }} />}
      <Typography variant="h4">{title}</Typography>
      {existingCampaign && <TcrCampaignInfo existingCampaign={existingCampaign} />}
      {!existingCampaign && (
        <FormTextField
          form={nonEditableForm}
          name="brandId"
          label="Brand ID"
          customValidate={/^B[A-Z0-9]{6}/}
          minLength={7}
          maxLength={7}
        />
      )}
      <FormTextField
        form={editableForm}
        name="description"
        label="Campaign description"
        minLength={40}
        maxLength={2048}
      />
      <FormTextField
        form={editableForm}
        name="messageFlow"
        helperText="How the lead is opted into the campaign"
        multiline
        minLength={40}
        maxLength={2048}
      />
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ gap: 2 }}>
        <FormTextField
          form={nonEditableForm}
          name="usecase"
          label="Use-case"
          disabled={!!existingCampaign}
        >
          <MenuItem value="MARKETING">Marketing</MenuItem>
          <MenuItem value="LOW_VOLUME">Low-volume Marketing</MenuItem>
        </FormTextField>
        <FormTextField form={editableForm} name="optinKeywords" label="Opt-in keywords" optional />
        <FormTextField form={editableForm} name="optoutKeywords" label="Opt-out keywords" />
        <FormTextField form={editableForm} name="helpKeywords" label="Help keywords" />
      </Box>
      <FormTextField form={editableForm} name="optoutMessage" label="Opt-out message" />
      <FormTextField form={editableForm} name="helpMessage" />
      <FormTextField
        form={editableForm}
        name="sample1"
        label="Sample message #1"
        minLength={20}
        multiline
      />
      <FormTextField
        form={editableForm}
        name="sample2"
        label="Sample message #2"
        minLength={20}
        multiline
      />
      <FormTextField
        form={editableForm}
        name="sample3"
        label="Sample message #3"
        minLength={20}
        multiline
        helperText="Fill this if there are multiple Aktify campaigns going to share the TCR campaign and that the scripting is different but the goal is the same"
        optional
      />
      <FormTextField
        form={editableForm}
        name="sample4"
        label="Sample message #4"
        minLength={20}
        multiline
        optional
      />
      <FormProvider {...nonEditableForm}>
        <TcrCheckboxGrid disabled={!!existingCampaign} />
      </FormProvider>
      {!existingCampaign && (
        <LabelWithValue
          label="Registering with MNOs"
          value={mnoOptions.map((x) => x.displayName).join(', ')}
          sx={{ marginTop: 4 }}
        />
      )}

      {!existingCampaign && (
        <FormTextField
          form={nonEditableForm}
          name="upstreamCnpId"
          label="Connectivity partner"
          sx={{ width: 200 }}
        >
          <MenuItem value={Cnp.Telnyx}>Telnyx</MenuItem>
        </FormTextField>
      )}
      {existingCampaign && <TcrSharingInfo sharing={sharing} Cnp={Cnp} />}
      {existingCampaign ? (
        <FormProvider {...editableForm}>
          <FormSubmitRow submitText="Save" />
        </FormProvider>
      ) : (
        <FormProvider {...nonEditableForm}>
          <FormSubmitRow submitText="Create" />
        </FormProvider>
      )}
    </form>
  );
};

export const TcrCard = ({ campaignId, internalCampaignId }: TcrCardProps) => {
  return (
    <Card variant="outlined" sx={{ marginTop: 2, padding: 2 }}>
      <TcrForm campaignId={campaignId} internalCampaignId={internalCampaignId} />
    </Card>
  );
};
